// Copyright 2025, University of Maryland, All Rights Reserved
import { useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowUpRight, Bot, Layers } from 'lucide-react'
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from '@/components/ui'
import { useAuth } from '@/subsystems/workspaces'
import { PipelineBuilder, useAgentPipeline, usePipelinesList } from '@/subsystems/pipelines'
import type { Pipeline, PipelineCanvasHandle } from '@/subsystems/pipelines'
import { useAgents } from '@/subsystems/agents'
import { useServices } from '@/app-shell/services'
import { ToolPalette } from '@/subsystems/palette'

/** Sentinel scope value — agent ids are prefixed to avoid colliding with it. */
const WORKSPACE_SCOPE = 'workspace'
const agentScope = (agentId: string) => `agent:${agentId}`

export default function PipelinesPage() {
  const navigate = useNavigate()
  const { selectedOrganizationId, selectedWorkspaceId } = useAuth()
  const services = useServices()
  const [isPipelineConfigMode, setIsPipelineConfigMode] = useState(false)
  const [mobilePaletteOpen, setMobilePaletteOpen] = useState(false)
  const canvasRef = useRef<PipelineCanvasHandle>(null)

  // Scope picks whose pipelines the builder edits: the workspace's connector
  // pipelines, or one agent's self-contained pipeline. It lives in the URL
  // (?agent=<id>) so agent settings can deep-link to an agent's pipeline.
  const [searchParams, setSearchParams] = useSearchParams()
  const agentId = searchParams.get('agent') ?? undefined
  const scope = agentId ? agentScope(agentId) : WORKSPACE_SCOPE

  const handleScopeChange = (value: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (value === WORKSPACE_SCOPE) {
        next.delete('agent')
      } else {
        next.set('agent', value.slice('agent:'.length))
      }
      // The canvas remounts on scope change; drop its node-config flag too.
      next.delete('pconfig')
      return next
    })
  }

  const { data: agents = [] } = useAgents(
    services.getClient(),
    selectedOrganizationId ?? '',
    selectedWorkspaceId ?? '',
  )

  const workspaceQuery = usePipelinesList(
    selectedOrganizationId ?? undefined,
    selectedWorkspaceId ?? undefined,
  )
  const agentQuery = useAgentPipeline(
    selectedOrganizationId ?? undefined,
    selectedWorkspaceId ?? undefined,
    agentId,
  )

  const agentPipelines = useMemo<Record<string, Pipeline>>(
    () => (agentQuery.data ? { [agentQuery.data.id]: agentQuery.data } : {}),
    [agentQuery.data],
  )

  const pipelines = agentId ? agentPipelines : workspaceQuery.data
  const loading = agentId ? agentQuery.isLoading : workspaceQuery.isLoading
  const error = agentId ? agentQuery.error : workspaceQuery.error

  if (!selectedOrganizationId || !selectedWorkspaceId) {
    return (
      <div className="flex h-64 items-center justify-center px-6 text-center">
        <p className="text-[13px] text-slate-500">
          Select an organization and workspace to use the pipeline builder.
        </p>
      </div>
    )
  }

  return (
    <div className="absolute inset-0 flex flex-col overflow-hidden bg-slate-50">
      <div className="flex flex-wrap items-center gap-2 px-4 pt-4">
        <span className="shrink-0 text-[13px] text-slate-500">Scope</span>
        <Select value={scope} onValueChange={handleScopeChange}>
          <SelectTrigger className="w-[220px] bg-white sm:w-[260px]" data-testid="builder-scope-selector">
            <SelectValue placeholder="Select scope" />
          </SelectTrigger>
          {/* popper keeps the list below the trigger instead of overlaying it
              (item-aligned can extend past the top of the viewport) */}
          <SelectContent position="popper">
            <SelectItem value={WORKSPACE_SCOPE}>Workspace</SelectItem>
            {agents.map(agent => (
              <SelectItem key={agent.agentId} value={agentScope(agent.agentId)}>
                <span className="flex items-center gap-1.5">
                  <Bot className="h-3.5 w-3.5 text-muted-foreground" />
                  {agent.label}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {agentId && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/agents/${agentId}`)}
          >
            <ArrowUpRight className="h-4 w-4 mr-2" />
            Go to Agent
          </Button>
        )}
        <span className="flex-1" />
        {!isPipelineConfigMode && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-[13px] md:hidden"
            aria-expanded={mobilePaletteOpen}
            onClick={() => setMobilePaletteOpen(open => !open)}
          >
            <Layers className="h-4 w-4" />
            Components
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-[13px] text-slate-500">Loading pipelines…</p>
        </div>
      ) : error ? (
        <div className="flex flex-1 items-center justify-center px-6">
          <div className="text-center">
            <p className="text-[13.5px] font-[550] text-red-600">Failed to load pipelines.</p>
            <p className="mt-1 text-[12.5px] text-slate-500">{error.message}</p>
          </div>
        </div>
      ) : (
        <div className={cn('flex min-h-0 flex-1 flex-col p-4 md:flex-row', !isPipelineConfigMode && 'gap-4')}>
          {!isPipelineConfigMode && (
            <div
              className={cn(
                'flex-shrink-0 overflow-hidden rounded-xl border border-[#e7ecf2] bg-white shadow-[0_1px_2px_rgba(2,8,23,0.04)]',
                'md:block md:h-full md:w-72',
                mobilePaletteOpen
                  ? 'h-[45vh] animate-in fade-in slide-in-from-top-2 duration-200'
                  : 'hidden',
              )}
            >
              {/* Tap-to-add stages the node at the canvas center — the only add
                  path on touch screens, and a convenient one everywhere */}
              <ToolPalette
                viewMode="pipeline"
                onNodeSelect={item => {
                  canvasRef.current?.requestAddNode(item.id)
                  setMobilePaletteOpen(false)
                }}
              />
            </div>
          )}

          <div className="min-h-0 min-w-0 flex-1">
            <PipelineBuilder
              key={scope}
              ref={canvasRef}
              workspaceId={selectedWorkspaceId}
              organizationId={selectedOrganizationId}
              agentId={agentId}
              pipelines={pipelines}
              onConfigModeChange={setIsPipelineConfigMode}
            />
          </div>
        </div>
      )}
    </div>
  )
}
